// F16 — the home page, repositioned around the two native apps.
//
// What was deleted here, and why:
//
// - The desktop download section. Its three buttons pointed at
//   /downloads/Aqana-Setup.exe, .dmg and .AppImage; none of those files ever
//   existed, so every one returned the SPA's index.html. A visitor clicking
//   "Download for Windows" received an HTML file. Electron is legacy; native
//   is the product.
// - The testimonials. They were invented — the business is pre-pilot and the
//   quoted customers do not exist. Beyond credibility, presenting fabricated
//   endorsements is a misleading commercial practice under the UK Consumer
//   Protection from Unfair Trading Regulations. They return when real pilot
//   quotes exist.
// - The "Aqana vs Traditional POS" comparison table, which asserted setup
//   "Under 10 minutes", "Software Installation: None required" and that
//   "hundreds of restaurants are making the switch". None of that is true.
// - The dead demo form state (formData was declared but never submitted —
//   there was no handler and no endpoint).
//
// Section order is now: hero, the three stations, back office, insights,
// pricing, one closing CTA.
import React from 'react';
import { Link } from 'react-router-dom';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, A, FONT, TYPE, SP, R, W, S } from '../../components/landing/tokens';
import ArchMark from '../../components/landing/ArchMark';
import StoreBadges from '../../components/landing/StoreBadges';

// auto-fit grids stack on their own below the min track, so the two-column
// and three-column sections need no media query and mirror cleanly in RTL.
const grid = (min) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
    gap: SP.xl,
});

const HomeLandingPage = () => {
    const { t } = useLanguage();

    const stations = [
        {
            src: '/screenshots/cashier-till.png',
            alt: t('The Aqana cashier till on an iPad, showing the product grid and a live cart'),
            title: t('Till'),
            body: t('The full menu, add-ons, splits and discounts. Card, cash or split payment, then the receipt prints.'),
        },
        {
            src: '/screenshots/waiter-phone.png',
            alt: t('A waiter taking an order on a phone in the Aqana app'),
            title: t('Table service'),
            body: t('Take the order at the table on a phone. It reaches the kitchen before the waiter does.'),
        },
        {
            src: '/screenshots/kitchen-display.png',
            alt: t('The Aqana kitchen display showing open tickets'),
            title: t('Kitchen'),
            body: t('Tickets land on screen as they are sent, in the order they came. Mark them off as they leave the pass.'),
        },
    ];

    const backOffice = [
        t('Build the rota, log advances, run the pay cycle.'),
        t('Track ingredients down to the recipe, so stock moves when a dish sells.'),
        t('Record daily and overhead expenses against the day they belong to.'),
    ];

    const insights = [
        t('See which dishes earn and which only sell.'),
        t('Compare a day against the same day last month.'),
        t('Watch takings by hour to staff the shifts you actually trade.'),
        t('Track expenses against revenue without a spreadsheet.'),
    ];

    // Pricing claims are deliberately narrow. Aqana licenses software: it does
    // not supply hardware, does not employ anyone's staff, and does not process
    // card payments. The earlier copy ("Every device in the venue, one price",
    // "No transaction fees") read as though we provided devices and had a
    // permanent no-fee policy — the second would also be contradicted the day
    // payments move in-house. Say what the subscription covers and nothing more.
    const pricing = [
        { title: t('Every till included'), body: t('One subscription covers every till and phone you run it on. We licence the software, not the hardware.') },
        { title: t('Every user included'), body: t('Add the whole team. There is no charge per member of staff.') },
        { title: t('Your own hardware'), body: t('Run it on the iPads, phones and card terminal you already have.') },
    ];

    const MarkedItem = ({ children }) => (
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: SP.md }}>
            <span style={{ marginBlockStart: 3 }}>
                <ArchMark size={15} color={C.inkFaint} />
            </span>
            <span style={{ ...TYPE.body, color: C.inkMuted }}>{children}</span>
        </li>
    );

    const framed = {
        width: '100%',
        height: 'auto',
        display: 'block',
        borderRadius: R.md,
        border: `1px solid ${C.line}`,
        background: C.surface,
    };

    // The three station captures have very different shapes — two landscape
    // iPad frames and one portrait phone. Dropped straight into a grid the
    // phone triples its card's height and strands its caption far below the
    // other two. A fixed-height box with object-fit: contain gives all three
    // the same image area and leaves the captions on one line.
    const shotBox = {
        height: 240,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: C.paper,
        border: `1px solid ${C.line}`,
        borderRadius: R.md,
        overflow: 'hidden',
        padding: SP.sm,
    };

    const shotImg = {
        maxHeight: '100%',
        maxWidth: '100%',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
    };

    return (
        <LandingLayout>
            {/* ---------- 1. Hero ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <div style={{ maxWidth: 760 }}>
                        <h1 style={{ ...TYPE.displayXl, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('One system, counter to books.')}
                        </h1>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, maxWidth: 560, marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                            {t('Take the order, print the ticket, pay the staff, see the margin. Aqana is the till and the back office, so nothing gets typed twice.')}
                        </p>

                        <StoreBadges app="pos" align="start" />

                        <p style={{ ...TYPE.caption, color: C.inkFaint, marginBlockStart: SP.lg }}>
                            {t('or')}{' '}
                            <a
                                href="/login"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: C.ink, textDecoration: 'none', borderBlockEnd: `1px solid ${C.lineStrong}` }}
                            >
                                {t('open Aqana in your browser')}
                            </a>{' '}
                            {t('— no install needed')}
                        </p>
                    </div>
                </div>
            </section>

            {/* ---------- 2. The three stations ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={S.container}>
                    <span style={S.label}>{t('THE FLOOR')}</span>
                    <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('Three stations, one order.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px ${SP['3xl']}px` }}>
                        {t('An order taken at the till, on a phone at the table, or sent to the kitchen screen is the same order. It does not get re-keyed at any point.')}
                    </p>

                    <div style={grid(300)}>
                        {stations.map((station) => (
                            <article key={station.title} style={{ ...S.card, padding: SP.lg }}>
                                <div style={shotBox}>
                                    <img src={station.src} alt={station.alt} loading="lazy" style={shotImg} />
                                </div>
                                <h3 style={{ ...TYPE.title, color: C.ink, marginBlock: `${SP.lg}px ${SP.sm}px` }}>
                                    {station.title}
                                </h3>
                                <p style={{ ...TYPE.body, color: C.inkMuted, margin: 0 }}>{station.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- 3. Back office ---------- */}
            <section style={S.section()}>
                <div style={{ ...S.container, ...grid(400), gap: SP['4xl'], alignItems: 'center' }}>
                    <div>
                        <span style={S.label}>{t('THE BACK OFFICE')}</span>
                        <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('The other half of the job.')}
                        </h2>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px ${SP.xl}px` }}>
                            {t('Rotas, stock, suppliers, wages and expenses live in the same system as the till. The Manager app puts them on your phone, and the full portal in any browser.')}
                        </p>
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md, marginBlockEnd: SP['2xl'] }}>
                            {backOffice.map((item) => <MarkedItem key={item}>{item}</MarkedItem>)}
                        </ul>
                        <StoreBadges app="manager" align="start" />
                    </div>

                    <div style={{ ...S.card, padding: SP.md }}>
                        <img
                            src="/screenshots/manager-dashboard.png"
                            alt={t('The Aqana manager portal dashboard showing revenue, orders and staff on shift')}
                            loading="lazy"
                            style={framed}
                        />
                    </div>
                </div>
            </section>

            {/* ---------- 4. Insights ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={{ ...S.container, ...grid(400), gap: SP['4xl'], alignItems: 'center' }}>
                    <div style={{ ...S.card, padding: SP.md }}>
                        <img
                            src="/screenshots/manager-analytics.png"
                            alt={t('The Aqana analytics view showing product performance and margin')}
                            loading="lazy"
                            style={framed}
                        />
                    </div>

                    <div>
                        <span style={S.label}>{t('INSIGHTS')}</span>
                        <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('Every sale, already counted.')}
                        </h2>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px ${SP.xl}px` }}>
                            {t('Because the till and the back office are one system, the numbers are ready without anyone exporting anything. Margin by dish, cost by supplier, takings by hour.')}
                        </p>
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md }}>
                            {insights.map((item) => <MarkedItem key={item}>{item}</MarkedItem>)}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ---------- 5. Pricing ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <span style={S.label}>{t('PRICING')}</span>
                    <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('One monthly price, per venue.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px ${SP['3xl']}px` }}>
                        {t('A single monthly subscription for the software, quoted per venue. No licence per till and none per member of staff, so adding either does not change the bill.')}
                    </p>

                    <div style={{ ...grid(240), gap: SP['2xl'] }}>
                        {pricing.map((item) => (
                            <div key={item.title} style={{ borderBlockStart: `1px solid ${C.line}`, paddingBlockStart: SP.lg }}>
                                <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{item.title}</h3>
                                <p style={{ ...TYPE.caption, color: C.inkFaint, marginBlockStart: SP.sm }}>{item.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- 6. Get a demo ---------- */}
            <section
                style={{
                    ...S.section(),
                    background: C.ink,
                    color: C.paper,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <ArchMark
                    size={420}
                    color={C.paper}
                    opacity={0.05}
                    style={{ position: 'absolute', insetInlineEnd: -60, insetBlockStart: -40, pointerEvents: 'none' }}
                />
                <div style={{ ...S.container, position: 'relative', textAlign: 'center' }}>
                    <h2 style={{ ...TYPE.displayL, color: C.paper, margin: 0, textWrap: 'balance' }}>
                        {t('See it on your own menu.')}
                    </h2>
                    <p style={{ ...TYPE.bodyL, color: A.paperMuted, maxWidth: 520, marginInline: 'auto', marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                        {t('We will set Aqana up with your products and prices and walk you through a service.')}
                    </p>
                    <Link
                        to="/contact-us"
                        style={{ ...S.btnPrimary, background: C.paper, color: C.ink, border: `1px solid ${C.paper}` }}
                    >
                        {t('Get a demo')}
                    </Link>
                </div>
            </section>
        </LandingLayout>
    );
};

export default HomeLandingPage;
